// frontend/src/context/CurrencyContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

// Exchange rate: 1 USD = 24.65 SLL
const DEFAULT_SLL_RATE = 24.65;

const CURRENCY_CONFIGS = {
  SLL: {
    code: "SLL",
    symbol: "Le",
    label: "Sierra Leone Leone",
    isPrimary: true,
    locale: "en-SL",
    rate: DEFAULT_SLL_RATE,
  },
  USD: {
    code: "USD",
    symbol: "$",
    label: "US Dollar",
    isPrimary: false,
    locale: "en-US",
    rate: 1,
  },
  MYR: {
    code: "MYR",
    symbol: "RM",
    label: "Malaysian Ringgit",
    isPrimary: false,
    locale: "ms-MY",
    rate: 4.75,
  },
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem("preferredCurrency");
    return saved && CURRENCY_CONFIGS[saved] ? saved : "SLL";
  });

  const [rates, setRates] = useState({
    USD: 1,
    SLL: DEFAULT_SLL_RATE,
    MYR: 4.75,
  });

  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Save currency preference
  useEffect(() => {
    localStorage.setItem("preferredCurrency", currency);
  }, [currency]);

  // Fetch exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(
          "https://api.exchangerate-api.com/v4/latest/USD",
          { cache: "no-cache" },
        );
        const data = await response.json();
        if (data.rates) {
          let sllRate = data.rates.SLL;
          if (sllRate > 1000) {
            sllRate = DEFAULT_SLL_RATE;
          }
          setRates({
            USD: 1,
            SLL: sllRate,
            MYR: data.rates.MYR || 4.75,
          });
          setLastUpdated(new Date());
          console.log(`✅ Frontend rates updated: 1 USD = ${sllRate} SLL`);
        }
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRates();
    const interval = setInterval(fetchRates, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Get current rate for a currency
  const getRate = useCallback(
    (currencyCode) => {
      return rates[currencyCode] || CURRENCY_CONFIGS[currencyCode]?.rate || 1;
    },
    [rates],
  );

  // Format amount: expects USD, returns formatted string in specified currency
  const formatAmount = useCallback(
    (amountUSD, options = {}) => {
      const { currency: targetCurrency = currency, showSymbol = true } =
        options;

      if (!amountUSD && amountUSD !== 0) {
        return showSymbol
          ? `${CURRENCY_CONFIGS[targetCurrency]?.symbol || "Le"} 0`
          : "0";
      }

      const rate = getRate(targetCurrency);
      const amount = amountUSD * rate;
      const config = CURRENCY_CONFIGS[targetCurrency] || CURRENCY_CONFIGS.SLL;
      const symbol = config?.symbol || "Le";

      const formatted = Math.round(amount).toLocaleString(
        config?.locale || "en-SL",
      );

      return showSymbol ? `${symbol}${formatted}` : formatted;
    },
    [currency, getRate],
  );

  // Convert USD to a target currency value (returns number)
  const convertPrice = useCallback(
    (amountUSD, targetCurrency = currency) => {
      if (!amountUSD && amountUSD !== 0) return 0;
      const rate = getRate(targetCurrency);
      return amountUSD * rate;
    },
    [currency, getRate],
  );

  // Format with currency symbol
  const formatCurrency = useCallback(
    (amountUSD, targetCurrency = currency) => {
      return formatAmount(amountUSD, {
        currency: targetCurrency,
        showSymbol: true,
      });
    },
    [formatAmount],
  );

  // Toggle between SLL and USD
  const toggleCurrency = useCallback(() => {
    setCurrency((prev) => (prev === "SLL" ? "USD" : "SLL"));
  }, []);

  // Get all prices for an amount
  const getAllPrices = useCallback(
    (amountUSD) => {
      if (!amountUSD && amountUSD !== 0) {
        return { usd: 0, sll: 0, myr: 0 };
      }
      return {
        usd: parseFloat(amountUSD.toFixed(2)),
        sll: parseFloat((amountUSD * rates.SLL).toFixed(2)),
        myr: parseFloat((amountUSD * rates.MYR).toFixed(2)),
      };
    },
    [rates],
  );

  const value = {
    currency,
    setCurrency,
    rates,
    loading,
    lastUpdated,
    formatAmount,
    formatCurrency,
    convertPrice,
    toggleCurrency,
    getSymbol: (code = currency) => CURRENCY_CONFIGS[code]?.symbol || "Le",
    getExchangeRate: getRate,
    getAllPrices,
    defaultCurrency: "SLL",
    CURRENCY_CONFIGS,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;
